import { Box, Button, Checkbox, Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr, useBoolean, useToast } from '@chakra-ui/react'
import { EmptyTablePlaceholder, TableSkeleton } from 'brokertslib/src/components'
import type { InsuranceParamsGridItem } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useState } from 'react'
import { Link } from 'react-router'

import { Pagination } from '../../components/Pagination'
import { TableWrapper } from '../../components/TableWrapper'
import { useApp } from '../../hooks/useApp'
import { useDeleteInsuranceParams } from '../../hooks/useInsuranceParams'
import { Trash } from '../../icons'
import { conceptsCarsContext } from '../../pages/carkulka/concepts'
import { conceptsTravelContext } from '../../pages/cestovko/concepts'
import { conceptsPropertyContext } from '../../pages/chaloupka/concepts'
import { ConceptRow } from './ConceptRow'

export const useConceptsContext = () => {
  const { app } = useApp()

  if (app === 'cestovko')
    return conceptsTravelContext

  if (app === 'chaloupka')
    return conceptsPropertyContext

  return conceptsCarsContext
}


export const ConceptsList = ({
  loading,
  concepts,
  withPaginator = false,
  allowSelect = false,
}: {
  loading?: boolean
  concepts: Array<InsuranceParamsGridItem>
  withPaginator?: boolean
  allowSelect?: boolean
}) => {
  const context = useConceptsContext()
  const { currentPage, pageSize, totalCount, setCurrentPage, reload, ...rest } = useContext(context)
  loading = loading || rest.loading

  const { app, product } = useApp()

  const [selectedRows, setSelectedRows] = useState<InsuranceParamsGridItem['insuranceParamsId'][]>([])
  const [confirmDelete, setConfirmDelete] = useBoolean(false)
  const [isDeleting, setIsDeleting] = useBoolean(false)
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < concepts.length
  const isChecked = selectedRows.length === concepts.length
  const toast = useToast()
  const { mutateAsync: deleteInsuranceParams } = useDeleteInsuranceParams()

  const handleCheckboxChange = () => {
    if (selectedRows.length === concepts.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(concepts.map((concept) => concept.insuranceParamsId))
    }
  }

  const handleRowSelect = (id: InsuranceParamsGridItem['insuranceParamsId']) => {
    const isSelected = selectedRows.includes(id)
    let newSelectedRows = selectedRows

    if (isSelected) {
      // Remove when is currently selected
      newSelectedRows = selectedRows.filter((rowId) => rowId !== id)
    } else {
      // Add when not selected yet
      newSelectedRows = [...newSelectedRows, id]
    }

    setSelectedRows(() => newSelectedRows)
  }

  const handleDelete = () => {
    if (confirmDelete) {
      setIsDeleting.on()
      deleteInsuranceParams({ paramIds: selectedRows })
        .then((resp) => {
          setIsDeleting.off()
          setSelectedRows([])
          setConfirmDelete.off()
          reload()
          if (!resp.data.success && resp.data.errors) {
            toast({
              title: 'Nepodařilo se smazat nabídku: ' + resp.data.errors?.join(),
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
        })
        .catch((err) => console.log('error delete param ids', err))
    } else {
      setConfirmDelete.on()
    }
  }

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <TableWrapper>
        <Table cellPadding="0" cellSpacing="0">
          <Thead>
            <Tr>
              <Th sx={{ display: allowSelect ? 'table-cell' : 'none' }}>
                <Checkbox
                  isIndeterminate={isIndeterminate}
                  isChecked={isChecked}
                  onChange={handleCheckboxChange}
                />
              </Th>

              <Th>
                <p>Číslo nabídky</p>
                <p>Stav</p>
              </Th>

              <Th>
                <p>Datum vytvoření</p>
                <p>Datum zahájení</p>
              </Th>

              <Th sx={{ px: 0 }}>{/* <!-- Space for initials --> */}</Th>

              <Th sx={{ pl: 0 }}>
                <p>Pojistník</p>
                {product == Product.VehicleInsurance && <p>Vozidlo</p>}
              </Th>

              {app == 'chaloupka' &&
                <Th>
                  <p>Objekt</p>
                </Th>
              }

              <Th>
                <p>Pojišťovna</p>
              </Th>

              <Th>
                <p>Název nabídky</p>
                <p>Zpracoval</p>
              </Th>

              <Th>{/* <!-- Space for status --> */}</Th>

              <Th>{/* <!-- Space for buttons --> */}</Th>
            </Tr>
          </Thead>

          <Tbody>
            <TableSkeleton colSpan={9} rowsCount={pageSize} isLoaded={!loading} rowHeight={10}>
              <EmptyTablePlaceholder text="Žádné nabídky k zobrazení" colSpan={9} >{
                concepts.map((concept) => {
                  return <ConceptRow
                    key={`${concept.insuranceParamsId}_${concept.offerId}`}
                    allowSelect={allowSelect}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    item={concept} />
                })
              }
              </EmptyTablePlaceholder>
            </TableSkeleton>
          </Tbody>

          <Tfoot>
            <Tr>
              <Td colSpan={9}>
                {withPaginator && (
                  <Flex sx={{ placeContent: 'center' }}>
                    <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={10} pageSize={pageSize} />
                  </Flex>
                )}

                {!withPaginator && (
                  <Link to={`/${app}/concepts`}>
                    <Button type="button" variant="outline" size="sm">
                      Více neuzavřených nabídek
                    </Button>
                  </Link>
                )}
              </Td>
            </Tr>
          </Tfoot>
        </Table>
      </TableWrapper >

      {allowSelect && selectedRows.length > 0 && (
        <Flex sx={{ gap: 2 }}>
          {confirmDelete && !isDeleting && (
            <Button variant="outline" onClick={setConfirmDelete.off}>
              Zrušit
            </Button>
          )}

          <Button
            variant={confirmDelete ? 'solid' : 'outline'}
            colorScheme="red"
            onClick={handleDelete}
            isDisabled={isDeleting}
          >
            <Trash />
            {!isDeleting &&
              (confirmDelete
                ? `Opravdu smazat? (${selectedRows.length})`
                : `Smazat (${selectedRows.length})`)}
            {isDeleting && 'Mazání...'}
          </Button>
        </Flex>
      )}
    </Box>
  )
}
